/**
 * Component for displaying a video track
 */
import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRecordingContext } from '../RecordingContext';
import { useLogger } from '@ui/shared/components/Logger';
import { LOGGER_LABEL } from '../utils/constants';

/**
 * VideoTrack component renders a video element with the LiveKit track attached
 *
 * @param {object} props - Component props
 * @param {object} props.trackRef - The LiveKit track reference
 * @param {object} props.videoRef - Ref to be attached to the video element for measurement
 * @param {object} props.style - Additional style properties for the video element
 * @param {function} props.onVideoLoaded - Callback when video is loaded and sized
 * @param {string} props.dataTrackSid - Track SID for data attribute (for analytics/debug)
 * @returns {React.ReactElement} - The rendered component
 */
const VideoTrack = ({
  trackRef,
  videoRef,
  style,
  onVideoLoaded,
  dataTrackSid,
}) => {
  const [aspect, setAspect] = useState(null); // aspect ratio (width / height)
  const { showTrackSid } = useRecordingContext();
  const logger = useLogger();
  
  // Log component mount/unmount
  useEffect(() => {
    logger.debug(LOGGER_LABEL, 'VideoTrack mounted', {
      trackSid: dataTrackSid,
      hasTrackRef: !!trackRef,
      hasPublication: !!trackRef?.publication
    });
    
    return () => {
      logger.debug(LOGGER_LABEL, 'VideoTrack unmounted', {
        trackSid: dataTrackSid
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadedMetadata = useCallback((e) => {
    try {
      if (e.target && e.target.videoWidth && e.target.videoHeight) {
        const aspectRatio = e.target.videoWidth / e.target.videoHeight;
        setAspect(aspectRatio);
        
        logger.debug(LOGGER_LABEL, 'Video metadata loaded', {
          trackSid: dataTrackSid,
          width: e.target.videoWidth,
          height: e.target.videoHeight,
          aspectRatio: aspectRatio.toFixed(2)
        });
      } else {
        logger.warn(LOGGER_LABEL, 'Video metadata loaded but dimensions missing', {
          trackSid: dataTrackSid,
          hasTarget: !!e.target,
          hasWidth: !!e.target?.videoWidth,
          hasHeight: !!e.target?.videoHeight
        });
      }
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error handling video metadata', {
        error,
        errorMessage: error.message,
        trackSid: dataTrackSid
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTrackSid]);

  // Handle video dimension changes (e.g., phone rotation)
  const handleResize = useCallback((e) => {
    try {
      if (e.target && e.target.videoWidth && e.target.videoHeight) {
        const newAspect = e.target.videoWidth / e.target.videoHeight;
        const oldAspect = aspect;
        
        setAspect(newAspect);
        
        logger.info(LOGGER_LABEL, 'Video dimensions changed', {
          trackSid: dataTrackSid,
          oldAspect: oldAspect?.toFixed(2),
          newAspect: newAspect.toFixed(2),
          width: e.target.videoWidth,
          height: e.target.videoHeight
        });
        
        // Trigger the onVideoLoaded callback to update position measurements
        if (onVideoLoaded) {
          try {
            onVideoLoaded();
          } catch (callbackError) {
            logger.error(LOGGER_LABEL, 'Error in onVideoLoaded callback', {
              error: callbackError,
              errorMessage: callbackError.message,
              trackSid: dataTrackSid
            });
          }
        }
      }
    } catch (error) {
      logger.error(LOGGER_LABEL, 'Error handling video resize', {
        error,
        errorMessage: error.message,
        trackSid: dataTrackSid
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onVideoLoaded, aspect, dataTrackSid]);

  // Guard against undefined trackRef
  if (!trackRef || !trackRef.publication) {
    logger.warn(LOGGER_LABEL, 'VideoTrack rendering placeholder - missing trackRef or publication', {
      trackSid: dataTrackSid,
      hasTrackRef: !!trackRef,
      hasPublication: !!trackRef?.publication
    });
    return <div style={style || {}} className="video-placeholder"></div>;
  }

  // Decide sizing based on aspect ratio
  let videoStyle = { ...style };
  if (aspect) {
    if (aspect >= 1) {
      // Wider than tall
      videoStyle.width = 'auto';
      videoStyle.height = '100%';
      videoStyle.maxHeight = '100%';
    } else {
      // Taller than wide
      videoStyle.width = 'auto';
      videoStyle.height = '100%';
      videoStyle.maxWidth = '100%';
    }
    videoStyle.objectFit = 'contain';
    videoStyle.display = 'block';
    videoStyle.margin = '0 auto';
  } else {
    // Fallback to fill container while loading
    videoStyle.width = '100%';
    videoStyle.height = '100%';
    videoStyle.objectFit = 'contain';
    videoStyle.display = 'block';
    videoStyle.margin = '0 auto';
  }

  return (
    <div
      data-track-sid={dataTrackSid}
      style={{ width: '100%', height: '100%', position: 'relative' }}
    >
      <video
        key={trackRef.publication.trackSid}
        ref={(el) => {
          try {
            // Assign to the measurement ref
            if (videoRef) {
              videoRef.current = el;
            }
            
            // Attach the LiveKit track
            if (el && trackRef.publication.track) {
              if (el.srcObject !== trackRef.publication.track.mediaStreamTrack) {
                try {
                  trackRef.publication.track.attach(el);
                  logger.debug(LOGGER_LABEL, 'Track attached to video element', {
                    trackSid: dataTrackSid,
                    trackKind: trackRef.publication.track.kind
                  });
                } catch (attachError) {
                  logger.error(LOGGER_LABEL, 'Failed to attach track to video element', {
                    error: attachError,
                    errorMessage: attachError.message,
                    trackSid: dataTrackSid
                  });
                }
              }
            } else if (el && !trackRef.publication.track) {
              logger.warn(LOGGER_LABEL, 'Video element ready but track not available', {
                trackSid: dataTrackSid,
                hasPublication: !!trackRef.publication
              });
            }
          } catch (error) {
            logger.error(LOGGER_LABEL, 'Error in video ref callback', {
              error,
              errorMessage: error.message,
              trackSid: dataTrackSid
            });
          }
        }}
        style={videoStyle}
        muted
        autoPlay
        playsInline
        onLoadedMetadata={handleLoadedMetadata}
        onResize={handleResize}
        onPlaying={onVideoLoaded}
      />
      {/* Overlay for trackId - only shown if showTrackSid is true */}
      {dataTrackSid && showTrackSid && (
        <div
          style={{
            position: 'absolute',
            top: 8,
            left: 8,
            background: 'rgba(255,255,255,0.95)',
            color: '#000',
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 14,
            fontWeight: 600,
            zIndex: 10,
            pointerEvents: 'none',
            boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
            fontFamily: 'monospace',
            letterSpacing: 0.5,
          }}
        >
          {dataTrackSid}
        </div>
      )}
    </div>
  );
};

VideoTrack.propTypes = {
  trackRef: PropTypes.object,
  videoRef: PropTypes.object,
  style: PropTypes.object,
  onVideoLoaded: PropTypes.func,
  dataTrackSid: PropTypes.string,
};

export default VideoTrack;
